// @ts-nocheck
import React, { useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useStore } from "@tanstack/react-form";
import { componentFormKey } from "./ComponentTreeEditor";
import { trpc } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  Wrench,
  Layers,
  Plus,
  Trash2,
  RefreshCw,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Hammer,
} from "lucide-react";
import { TOOLING_STANDARDS } from "../tabs/POToolingTab";

import { toVnDateString } from "@apv-erp/status";
export interface ReusableToolingItem {
  id: string;
  toolingId: string;
  toolingName?: string;
  toolingType: "DIE_CUT" | "FOIL_STAMP" | "EMBOSS_DIE" | "DEBOSS_DIE" | "SPOT_UV" | "SILK_SCREEN";
  status: "REUSE" | "NEW" | "REPAIR";
  location?: string;
  notes?: string;
  dimensions?: string;
  /** Số khuôn */
  quantity?: number;
  /** Kiểu bế (khuôn bế / bế nổi / bế chìm) */
  dieCutStyle?: string;
  /** Diện tích ép kim (cm²) — khuôn ép nhũ */
  areaCm2?: number | string | null;
  /** Ngày cần (yyyy-MM-dd) */
  expectedDate?: string;
  /** Hợp phần dùng khuôn (id đã lưu hoặc clientKey hợp phần mới; trống = dùng chung) */
  componentKey?: string | null;
  /** Dòng gốc từ lệnh — giữ mã khuôn, công đoạn, trạng thái sẵn sàng, đơn đặt ngoài khi ghi lại */
  raw?: any;
}

const toDateInput = (v: any) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : toVnDateString(d);
};

/** Kiểu bế: chọn nhanh, "Khác" để nhập tự do */
export const DIE_CUT_STYLE_OPTIONS = ["Bế đứt", "Bế demi", "Bế xé", "Bế nổi", "Bế chìm"];
const DIE_TYPES = ["DIE_CUT", "EMBOSS_DIE", "DEBOSS_DIE"];

const TOOLING_TYPE_OPTIONS = [
  { value: "DIE_CUT", label: "Khuôn Bế Hộp (Die Cut)", prefix: "KB" },
  { value: "FOIL_STAMP", label: "Khuôn Ép Nhũ / Kim (Foil Stamp)", prefix: "EN" },
  { value: "EMBOSS_DIE", label: "Khuôn Bế Nổi / Thúc Nổi (Embossing)", prefix: "BN" },
  { value: "DEBOSS_DIE", label: "Khuôn Bế Chìm (Debossing)", prefix: "BC" },
  { value: "SPOT_UV", label: "Khuôn / Bản UV Định Hình (Spot UV)", prefix: "UV" },
  { value: "SILK_SCREEN", label: "Khuôn In Lụa (Silk Screen)", prefix: "KL" },
];

export function ToolingBuilder({
  form,
  productionOrderId,
  productDimensions,
}: {
  form: any;
  productionOrderId?: string;
  productDimensions?: {
    length?: number | null;
    width?: number | null;
    height?: number | null;
  };
}) {
  const selectedProductId = useStore(
    form.store,
    (s: any) => s.values.productId,
  );
  const selectedCustomerId = useStore(
    form.store,
    (s: any) => s.values.customerId,
  );

  const { options: dbMoldOptions } = useConfigLookup("mold_types");
  const toolingTypeOptions = useMemo(() => {
    if (dbMoldOptions && dbMoldOptions.length > 0) {
      return dbMoldOptions.map((opt) => ({
        value: opt.id,
        label: opt.label,
        prefix: opt.id.slice(0, 2).toUpperCase(),
      }));
    }
    return TOOLING_TYPE_OPTIONS;
  }, [dbMoldOptions]);

  // Read raw toolings from form
  const rawToolings = useStore(form.store, (s: any) => s.values.toolings) || [];
  // Cây hợp phần: lệnh nhiều hợp phần thì mỗi dòng chọn hợp phần dùng (trống = dùng chung cả lệnh)
  const formComponents: any[] = (useStore(form.store, (s: any) => s.values.components) || []).filter((c: any) => !c?.isDeleted);
  const treeMode = formComponents.length > 1 || formComponents.some((c: any) => c.parentKey || c.parentComponentId);

  // Filter for reusable mold items (excluding CTP plates)
  const toolingItems: ReusableToolingItem[] = useMemo(() => {
    return rawToolings
      .filter((t: any) => t.toolingType !== "PLATE" && t.toolingType !== "ZINC")
      .map((t: any, idx: number) => {
        let type = t.toolingType || "DIE_CUT";
        if (type === "PLATE" || type === "ZINC") type = "DIE_CUT";

        let status = t.status || "REUSE";
        if (status !== "REUSE" && status !== "NEW" && status !== "REPAIR") {
          status = "REUSE";
        }

        return {
          id: t.id || t.toolingId || `mold-${idx}-${Date.now()}`,
          toolingId: t.toolingId || t.id || "",
          toolingName: t.toolingName || t.name || t.dieCut?.name || "",
          toolingType: type,
          status,
          location: t.location || "",
          notes: t.notes || "",
          dimensions: t.dimensions || "",
          quantity: Number(t.quantity) || 1,
          dieCutStyle: t.dieCutStyle || "",
          areaCm2: t.areaCm2 !== null && t.areaCm2 !== undefined && t.areaCm2 !== "" ? Number(t.areaCm2) : null,
          expectedDate: toDateInput(t.expectedDate),
          componentKey: t.componentKey ?? t.componentId ?? "",
          raw: t,
        };
      });
  }, [rawToolings]);

  // Synchronize back to form (preserving CTP plates)
  const commitToolingItems = (newMolds: ReusableToolingItem[]) => {
    const existingPlates = (form.getFieldValue("toolings") || []).filter(
      (t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC"
    );

    const mappedMolds = newMolds.map((m, idx) => ({
      ...(m.raw || {}),
      id: m.id || `mold-${idx + 1}`,
      toolingId: m.toolingId || `MOLD-${idx + 1}`,
      toolingName: m.toolingName,
      toolingType: m.toolingType,
      status: m.status,
      location: m.location,
      notes: m.notes,
      dimensions: m.dimensions,
      quantity: m.quantity ?? 1,
      dieCutStyle: m.dieCutStyle || null,
      areaCm2: m.areaCm2 === "" ? null : (m.areaCm2 ?? null),
      expectedDate: m.expectedDate || null,
      componentKey: m.componentKey || null,
      componentId: m.componentKey && !String(m.componentKey).startsWith("new-comp-") ? m.componentKey : null,
      isUserCustomized: true,
    }));

    const nextToolings = [...existingPlates, ...mappedMolds];
    form.setFieldValue("toolings", nextToolings);

    // Keep dieCutIds in sync
    const nextDieCuts = mappedMolds
      .filter((m) => m.toolingType === "DIE_CUT")
      .map((m) => m.toolingId);
    form.setFieldValue("dieCutIds", nextDieCuts);

    if (productionOrderId) {
      toast.info("Đã cập nhật kế hoạch khuôn gá", { duration: 1200 });
    }
  };

  const { data: fullProduct, isLoading } = useQuery(
    trpc.products.getById.queryOptions(
      { id: selectedProductId },
      { enabled: !!selectedProductId }
    )
  );

  // Auto-fill molds from product's dieCuts
  const handleAutoFill = () => {
    if (!fullProduct) return;

    const newMolds: ReusableToolingItem[] = [];

    // Thêm Khuôn bế từ Sản phẩm
    if (fullProduct.dieCuts && Array.isArray(fullProduct.dieCuts)) {
      fullProduct.dieCuts.forEach((dc: any, idx: number) => {
        if (!dc.isDeleted && dc.status !== "DELETED") {
          newMolds.push({
            id: dc.id || `dc-${idx}`,
            toolingId: dc.id || dc.dieCutId || "",
            toolingName: dc.name || dc.code || "",
            toolingType: "DIE_CUT",
            status: "REUSE",
            location: dc.location || "",
            notes: dc.name || dc.code || "",
            dimensions: dc.dimensions || dc.size || (dc.productLength && dc.productWidth ? `${dc.productLength}x${dc.productWidth}mm` : ""),
          });
        }
      });
    }

    // Nếu sản phẩm có ép nhũ hoặc bế nổi nhưng chưa có khuôn gán, gợi ý thêm khuôn mới
    if (fullProduct.technicalSpec?.hasFoil && !newMolds.some((m) => m.toolingType === "FOIL_STAMP")) {
      newMolds.push({
        id: `foil-${Date.now()}`,
        toolingId: "",
        toolingType: "FOIL_STAMP",
        status: "NEW",
        location: "",
        notes: `Khuôn ép nhũ (${fullProduct.technicalSpec?.foilColor || "Vàng kim"})`,
      });
    }
    if (fullProduct.technicalSpec?.hasEmboss && !newMolds.some((m) => m.toolingType === "EMBOSS_DIE")) {
      newMolds.push({
        id: `emboss-${Date.now()}`,
        toolingId: "",
        toolingType: "EMBOSS_DIE",
        status: "NEW",
        location: "",
        notes: "Khuôn thúc nổi logo / họa tiết",
      });
    }

    if (newMolds.length > 0) {
      commitToolingItems(newMolds);
      toast.success(`Đã nạp ${newMolds.length} khuôn từ thông số Sản phẩm!`);
    } else {
      toast.warning("Sản phẩm chưa có liên kết khuôn bế sẵn. Vui lòng thêm thủ công.");
    }
  };

  const handleAddToolingRow = (type: any = "DIE_CUT") => {
    const newRow: ReusableToolingItem = {
      id: `mold-${Date.now()}`,
      toolingId: "",
      toolingType: type,
      status: "REUSE",
      location: "",
      notes: "",
    };
    commitToolingItems([...toolingItems, newRow]);
  };

  const handleUpdateRow = (index: number, updates: Partial<ReusableToolingItem>) => {
    const nextList = [...toolingItems];
    nextList[index] = { ...nextList[index], ...updates };
    commitToolingItems(nextList);
  };

  const handleRemoveRow = (index: number) => {
    const nextList = toolingItems.filter((_, idx) => idx !== index);
    commitToolingItems(nextList);
  };

  // Metrics
  const totalMolds = toolingItems.length;
  const totalMoldQty = toolingItems.reduce((n, m) => n + Math.max(1, Number(m.quantity) || 1), 0);
  const reuseCount = toolingItems.filter((m) => m.status === "REUSE").length;
  const newCount = toolingItems.filter((m) => m.status === "NEW").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div className="text-xs text-slate-600">
          <span className="font-bold text-slate-800 inline-flex items-center gap-1.5">
            <Wrench className="h-4 w-4 text-emerald-600" /> Khuôn &amp; đồ gá
          </span>
          <span className="ml-2">{totalMolds} dòng · {totalMoldQty} khuôn ({reuseCount} tái sử dụng, {newCount} làm mới)</span>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium h-8 text-xs"
            onClick={handleAutoFill}
            disabled={!fullProduct || isLoading}
          >
            {isLoading ? (
              <span className="h-3.5 w-3.5 mr-1 animate-spin rounded-full border-2 border-indigo-500 border-r-transparent" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1 text-indigo-600" />
            )}
            Tải khuôn từ Sản phẩm
          </Button>

          <Button
            type="button"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium h-8 text-xs"
            onClick={() => handleAddToolingRow("DIE_CUT")}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Thêm dòng khuôn
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto" data-testid="po-form-molds">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow className="border-b border-slate-200 text-[11px]">
                <TableHead className="w-10 text-center font-bold text-slate-600">#</TableHead>
              {treeMode && <TableHead className="font-bold text-slate-600 min-w-[130px]">Hợp phần</TableHead>}
                <TableHead className="font-bold text-slate-600 w-[200px]">Loại Khuôn / Công Đoạn</TableHead>
                <TableHead className="font-bold text-slate-600 min-w-[260px]">Tra Cứu Khuôn Trong Kho</TableHead>
                <TableHead className="font-bold text-slate-600 w-[160px]">Vị Trí Kệ Kho</TableHead>
                <TableHead className="font-bold text-slate-600 w-[150px]">Trạng Thái Xử Lý</TableHead>
                <TableHead className="font-bold text-slate-600 min-w-[200px]">Thông số (kiểu bế / DT ép kim)</TableHead>
                <TableHead className="font-bold text-slate-600 w-[80px] text-right">SL</TableHead>
                <TableHead className="font-bold text-slate-600 min-w-[140px]">Ngày cần</TableHead>
                <TableHead className="font-bold text-slate-600 min-w-[150px]">Ghi chú</TableHead>
                <TableHead className="w-12 text-right pr-3 font-bold text-slate-600">Xóa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {toolingItems.length > 0 ? (
                toolingItems.map((tool, index) => {
                  const dieCutParams = {
                    productId: selectedProductId || undefined,
                    type: tool.toolingType === "DIE_CUT" ? "DIE_CUT" : undefined,
                    ...(productDimensions
                      ? {
                          productLength: productDimensions.length,
                          productWidth: productDimensions.width,
                          customerId: selectedCustomerId || undefined,
                        }
                      : {}),
                  };

                  return (
                    <TableRow key={tool.id || index} data-testid="po-form-mold-row" className="hover:bg-slate-50/60 border-b border-slate-100">
                      <TableCell className="text-center font-mono text-xs text-slate-400">
                        {index + 1}
                      </TableCell>
                  {treeMode && (
                    <TableCell>
                      <select
                        aria-label="Hợp phần"
                        data-testid="po-form-tooling-component"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                        value={tool.componentKey || ""}
                        onChange={(e) => handleUpdateRow(index, { componentKey: e.target.value || "" })}
                      >
                        <option value="">Dùng chung</option>
                        {formComponents.map((c: any, ci: number) => {
                          const k = componentFormKey(c);
                          return k ? <option key={k} value={k}>{c.componentName || `Hợp phần ${ci + 1}`}</option> : null;
                        })}
                      </select>
                    </TableCell>
                  )}

                      {/* Loại Khuôn */}
                      <TableCell>
                        <Select
                          value={tool.toolingType}
                          onValueChange={(val: any) => handleUpdateRow(index, { toolingType: val })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-transparent">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {toolingTypeOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                <span className="font-bold font-mono mr-1.5 text-indigo-600">
                                  [{opt.prefix}]
                                </span>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>

                      {/* Tra Cứu Khuôn Kho */}
                      <TableCell>
                        <div className="h-8 flex items-center">
                          <SharedCombobox
                            type="dieCut"
                            params={dieCutParams}
                            value={tool.toolingId || ""}
                            valueLabel={tool.toolingName || tool.notes || (tool.toolingId ? `Khuôn ${tool.toolingId}` : undefined)}
                            onChange={(id, opt) => handleUpdateRow(index, {
                              toolingId: id,
                              toolingName: opt?.label || tool.toolingName,
                              notes: tool.notes || opt?.label,
                            })}
                            placeholder={selectedProductId ? "Chọn mã khuôn từ kho..." : "Chọn SP trước..."}
                            canAdd={true}
                          />
                        </div>
                      </TableCell>

                      {/* Vị Trí Kệ Kho */}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                          <Input
                            value={tool.location || ""}
                            onChange={(e) => handleUpdateRow(index, { location: e.target.value })}
                            className="h-8 text-xs font-mono font-medium text-slate-800 bg-transparent"
                            placeholder="Vd: Kệ A-01, K02..."
                          />
                        </div>
                      </TableCell>

                      {/* Trạng Thái Xử Lý */}
                      <TableCell>
                        <Select
                          value={tool.status}
                          onValueChange={(val: any) => handleUpdateRow(index, { status: val })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-transparent">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="REUSE" className="text-xs">
                              <span className="flex items-center text-blue-700 font-medium">
                                <CheckCircle2 className="h-3 w-3 mr-1 text-blue-600" /> Tái sử dụng (REUSE)
                              </span>
                            </SelectItem>
                            <SelectItem value="NEW" className="text-xs">
                              <span className="flex items-center text-amber-700 font-medium">
                                <Hammer className="h-3 w-3 mr-1 text-amber-600" /> Làm mới (NEW)
                              </span>
                            </SelectItem>
                            <SelectItem value="REPAIR" className="text-xs">
                              <span className="flex items-center text-rose-700 font-medium">
                                <AlertCircle className="h-3 w-3 mr-1 text-rose-600" /> Sửa chữa (REPAIR)
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>

                      {/* Thông số theo loại khuôn: kiểu bế + số khuôn / diện tích ép kim */}
                      <TableCell data-testid="tooling-spec-cell">
                        {DIE_TYPES.includes(tool.toolingType) ? (
                          <div className="flex items-center gap-1.5">
                            <select
                              data-testid="tooling-die-cut-style"
                              aria-label="Kiểu bế"
                              value={
                                !tool.dieCutStyle
                                  ? ""
                                  : DIE_CUT_STYLE_OPTIONS.includes(tool.dieCutStyle)
                                    ? tool.dieCutStyle
                                    : "__OTHER__"
                              }
                              onChange={(e) =>
                                handleUpdateRow(index, {
                                  dieCutStyle: e.target.value === "__OTHER__" ? "Khác: " : e.target.value,
                                })
                              }
                              className="h-8 w-[104px] shrink-0 rounded-md border border-slate-200 bg-white px-1.5 text-xs text-slate-700"
                            >
                              <option value="">Kiểu bế...</option>
                              {DIE_CUT_STYLE_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                              <option value="__OTHER__">Khác...</option>
                            </select>
                            {tool.dieCutStyle && !DIE_CUT_STYLE_OPTIONS.includes(tool.dieCutStyle) && (
                              <Input
                                aria-label="Kiểu bế khác"
                                value={tool.dieCutStyle.replace(/^Khác:\s*/, "")}
                                onChange={(e) => handleUpdateRow(index, { dieCutStyle: `Khác: ${e.target.value}` })}
                                className="h-8 w-[110px] text-xs"
                                placeholder="Nhập kiểu bế"
                              />
                            )}
                          </div>
                        ) : tool.toolingType === "FOIL_STAMP" ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min={0}
                              step="0.1"
                              aria-label="Diện tích ép kim (cm²)"
                              value={tool.areaCm2 ?? ""}
                              onChange={(e) => handleUpdateRow(index, { areaCm2: e.target.value === "" ? null : Number(e.target.value) })}
                              className="h-8 w-[90px] text-xs text-right"
                              placeholder="DT ép kim"
                            />
                            <span className="text-[11px] text-slate-500">cm²</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>

                      {/* SL khuôn */}
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          aria-label="Số khuôn"
                          data-testid="po-form-mold-qty"
                          value={tool.quantity ?? 1}
                          onChange={(e) => handleUpdateRow(index, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                          className="h-8 w-[64px] text-xs text-right"
                        />
                      </TableCell>

                      {/* Ngày cần */}
                      <TableCell>
                        <Input
                          type="date"
                          value={tool.expectedDate || ""}
                          onChange={(e) => handleUpdateRow(index, { expectedDate: e.target.value })}
                          className="h-8 text-xs bg-transparent"
                        />
                      </TableCell>

                      {/* Ghi chú */}
                      <TableCell>
                        <Input
                          value={tool.notes || ""}
                          onChange={(e) => handleUpdateRow(index, { notes: e.target.value })}
                          className="h-8 text-xs text-slate-600 bg-transparent"
                          placeholder="Ghi chú khuôn theo bài..."
                        />
                      </TableCell>

                      {/* Nút Xóa */}
                      <TableCell className="text-right pr-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={treeMode ? 11 : 10} className="text-center py-10 text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Wrench className="h-8 w-8 text-slate-300" />
                      <p className="text-xs font-medium">Chưa có khuôn bế hoặc đồ gá nào được cấu hình</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-1 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={handleAutoFill}
                        disabled={!fullProduct || isLoading}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Tải khuôn từ Sản phẩm
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </div>
    </div>
  );
}
